import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Eye, FileDown, Trash2, FileText } from 'lucide-react';
import { devisApi } from '../../utils/api';
import EnvoiDocumentButton from '../../components/EnvoiDocumentButton';
import { useApp } from '../../context/AppContext';
import { getStatutBadgeClass, getStatutLabel, formatMontant } from '../../utils/calculs';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function DevisList() {
  const { getApiParams, structures } = useApp();
  const [devis, setDevis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatut, setFilterStatut] = useState('en_cours'); // pipeline actif par défaut
  const [filterStructure, setFilterStructure] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    // On masque les devis des enfants déjà inscrits (actifs), sauf si on consulte explicitement les acceptés/refusés
    const exclureInscrits = (!filterStatut || filterStatut === 'en_cours') ? '1' : undefined;
    const params = {
      ...getApiParams(),
      ...(filterStatut && { statut: filterStatut }),
      ...(filterStructure && { structure_id: filterStructure }),
      ...(exclureInscrits && { exclure_inscrits: exclureInscrits }),
    };
    devisApi.getAll(params)
      .then(res => setDevis(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getApiParams, filterStatut, filterStructure]);

  useEffect(() => { load(); }, [load]);

  const formatDate = (d) => {
    if (!d) return '—';
    try { return format(parseISO(d), 'd MMM yyyy', { locale: fr }); }
    catch { return d; }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce devis ?')) return;
    try {
      await devisApi.delete(id);
      load();
    } catch (err) {
      alert('Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={filterStatut}
            onChange={e => setFilterStatut(e.target.value)}
            className="form-select w-40 text-sm"
          >
            <option value="">Tous les statuts</option>
            <option value="en_cours">En cours</option>
            <option value="accepte">Accepté</option>
            <option value="refuse">Refusé</option>
            <option value="expire">Expiré</option>
          </select>
          <select
            value={filterStructure}
            onChange={e => setFilterStructure(e.target.value)}
            className="form-select w-52 text-sm"
          >
            <option value="">Toutes les structures</option>
            {structures.map(s => (
              <option key={s.id} value={s.id}>{s.nom}</option>
            ))}
          </select>
        </div>
        <Link to="/devis/nouveau" className="btn-primary">
          <Plus size={16} /> Nouveau devis
        </Link>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-400">Chargement...</div>
        ) : devis.length === 0 ? (
          <div className="py-16 text-center">
            <FileText size={40} className="text-gray-300 mx-auto mb-3" />
            <div className="text-gray-400 text-sm">Aucun devis trouvé</div>
            <Link to="/devis/nouveau" className="btn-primary mt-4 inline-flex">
              <Plus size={16} /> Nouveau devis
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">N°</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Enfant</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Structure</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Date</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Heures/sem</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Tarif</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Mensualité</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Statut</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {devis.map((d, i) => (
                  <tr
                    key={d.id}
                    className={`border-b border-gray-50 hover:bg-gray-50 ${i % 2 !== 0 ? 'bg-gray-50/30' : ''}`}
                  >
                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">{d.numero ? d.numero : `#${String(d.id).padStart(4, '0')}`}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {d.enfant_prenom} {d.enfant_nom}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{d.structure_nom}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(d.date_devis)}</td>
                    <td className="px-4 py-3 text-gray-700">{(d.heures_hebdo || 0).toFixed(1)}h</td>
                    <td className="px-4 py-3 text-gray-700">{formatMontant(d.tarif_horaire)}/h</td>
                    <td className="px-4 py-3 font-semibold text-primary-600">
                      {formatMontant(d.montant_mensuel)}/mois
                    </td>
                    <td className="px-4 py-3">
                      <span className={getStatutBadgeClass(d.statut)}>{getStatutLabel(d.statut)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Link to={`/devis/${d.id}`} className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-primary-500" title="Voir">
                          <Eye size={16} />
                        </Link>
                        <a
                          href={devisApi.getPdfUrl(d.id)}
                          target="_blank"
                          rel="noreferrer"
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-500"
                          title="Télécharger PDF"
                        >
                          <FileDown size={16} />
                        </a>
                        <EnvoiDocumentButton type="devis" refId={d.id} enfantId={d.enfant_id} compact />
                        <button
                          onClick={() => handleDelete(d.id)}
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-500"
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {!loading && (
        <div className="text-xs text-gray-400">{devis.length} devis trouvé{devis.length > 1 ? 's' : ''}</div>
      )}
    </div>
  );
}
